import { lazy, Suspense, type ReactNode, memo } from 'react';
import { Navigate, type RouteObject } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout';
import { PageSkeleton } from '@/components/feedback/LoadingSkeletons';

// Lazy load all page components for code splitting
const LoginPage = lazy(() =>
  import('@/pages/LoginPage').then((module) => ({ default: module.LoginPage }))
);
const DashboardPage = lazy(() =>
  import('@/pages/DashboardPage').then((module) => ({ default: module.DashboardPage }))
);
const DoctorsListPage = lazy(() =>
  import('@/pages/doctors/DoctorsListPage').then((module) => ({
    default: module.DoctorsListPage,
  }))
);
const DoctorProfilePage = lazy(() =>
  import('@/pages/doctors/DoctorProfilePageNew').then((module) => ({
    default: module.DoctorProfilePageNew,
  }))
);
const AddEditDoctorPage = lazy(() =>
  import('@/pages/doctors/AddEditDoctorPage').then((module) => ({
    default: module.AddEditDoctorPage,
  }))
);
const TaskDetailsPage = lazy(() =>
  import('@/pages/tasks/TaskDetailsPage').then((module) => ({
    default: module.TaskDetailsPage,
  }))
);
const TaskPreviewPage = lazy(() =>
  import('@/pages/tasks/TaskPreviewPage').then((module) => ({
    default: module.TaskPreviewPage,
  }))
);

// Billing Cuts pages
const BillingCutsListPage = lazy(() =>
  import('@/features/billing-cuts').then((module) => ({
    default: module.BillingCutsListPage,
  }))
);
const GenerateBillingCutPage = lazy(() =>
  import('@/features/billing-cuts').then((module) => ({
    default: module.GenerateBillingCutPage,
  }))
);
const BillingCutDetailPage = lazy(() =>
  import('@/features/billing-cuts').then((module) => ({
    default: module.BillingCutDetailPage,
  }))
);
// const DocumentsPage = lazy(() =>
//   import('@/pages/DocumentsPage').then((module) => ({ default: module.DocumentsPage }))
// );
// const InvoicesPage = lazy(() =>
//   import('@/pages/InvoicesPage').then((module) => ({ default: module.InvoicesPage }))
// );
// const PaymentsPage = lazy(() =>
//   import('@/pages/PaymentsPage').then((module) => ({ default: module.PaymentsPage }))
// );
const TasksListPage = lazy(() =>
  import('@/features/tasks').then((module) => ({ default: module.TasksListPage }))
);

// Directory
const DirectoryPage = lazy(() =>
  import('@/features/directory/components/DirectoryPage').then((module) => ({
    default: module.DirectoryPage,
  }))
);

// Filter Examples (temporary for demo)
const FilterExamplesPage = lazy(() =>
  import('@/pages/FilterExamplesPage').then((module) => ({ default: module.FilterExamplesPage }))
);
// const ReportsPage = lazy(() =>
//   import('@/pages/ReportsPage').then((module) => ({ default: module.ReportsPage }))
// );
// const SettingsPage = lazy(() =>
//   import('@/pages/SettingsPage').then((module) => ({ default: module.SettingsPage }))
// );

// Wrapper component for Suspense - memoized to prevent unnecessary re-renders
const SuspenseWrapper = memo(function SuspenseWrapper({ children }: { children: ReactNode }) {
  return <Suspense fallback={<PageSkeleton />}>{children}</Suspense>;
});

// Protected route wrapper - redirects to login if not authenticated
interface ProtectedRouteProps {
  children: ReactNode;
  isAuthenticated: boolean;
}

const ProtectedRoute = memo(function ProtectedRoute({
  children,
  isAuthenticated,
}: ProtectedRouteProps) {
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
});

// Layout wrapper for authenticated routes - memoized
interface AuthenticatedLayoutProps {
  children: ReactNode;
  onLogout: () => void;
}

const AuthenticatedLayout = memo(function AuthenticatedLayout({
  children,
  onLogout,
}: AuthenticatedLayoutProps) {
  return <DashboardLayout onLogout={onLogout}>{children}</DashboardLayout>;
});

// Create routes configuration
export function createAppRoutes(
  isAuthenticated: boolean,
  onLogout: () => void,
  onLogin?: () => void
): RouteObject[] {
  return [
    {
      path: '/login',
      element: (
        <SuspenseWrapper>
          {isAuthenticated ? (
            <Navigate to="/dashboard" replace />
          ) : (
            <LoginPage onLogin={onLogin || (() => {})} />
          )}
        </SuspenseWrapper>
      ),
    },
    {
      path: '/',
      element: (
        <ProtectedRoute isAuthenticated={isAuthenticated}>
          <AuthenticatedLayout onLogout={onLogout}>
            <SuspenseWrapper>
              <Navigate to="/dashboard" replace />
            </SuspenseWrapper>
          </AuthenticatedLayout>
        </ProtectedRoute>
      ),
    },
    {
      path: '/dashboard',
      element: (
        <ProtectedRoute isAuthenticated={isAuthenticated}>
          <AuthenticatedLayout onLogout={onLogout}>
            <SuspenseWrapper>
              <DashboardPage />
            </SuspenseWrapper>
          </AuthenticatedLayout>
        </ProtectedRoute>
      ),
    },
    // Filter Examples (temporary route for demo)
    {
      path: '/filter-examples',
      element: (
        <ProtectedRoute isAuthenticated={isAuthenticated}>
          <AuthenticatedLayout onLogout={onLogout}>
            <SuspenseWrapper>
              <FilterExamplesPage />
            </SuspenseWrapper>
          </AuthenticatedLayout>
        </ProtectedRoute>
      ),
    },
    {
      path: '/doctors',
      element: (
        <ProtectedRoute isAuthenticated={isAuthenticated}>
          <AuthenticatedLayout onLogout={onLogout}>
            <SuspenseWrapper>
              <DoctorsListPage />
            </SuspenseWrapper>
          </AuthenticatedLayout>
        </ProtectedRoute>
      ),
    },
    {
      path: '/doctors/new',
      element: (
        <ProtectedRoute isAuthenticated={isAuthenticated}>
          <AuthenticatedLayout onLogout={onLogout}>
            <SuspenseWrapper>
              <AddEditDoctorPage />
            </SuspenseWrapper>
          </AuthenticatedLayout>
        </ProtectedRoute>
      ),
    },
    {
      path: '/doctors/:id',
      element: (
        <ProtectedRoute isAuthenticated={isAuthenticated}>
          <AuthenticatedLayout onLogout={onLogout}>
            <SuspenseWrapper>
              <DoctorProfilePage />
            </SuspenseWrapper>
          </AuthenticatedLayout>
        </ProtectedRoute>
      ),
    },
    {
      path: '/doctors/:id/edit',
      element: (
        <ProtectedRoute isAuthenticated={isAuthenticated}>
          <AuthenticatedLayout onLogout={onLogout}>
            <SuspenseWrapper>
              <AddEditDoctorPage />
            </SuspenseWrapper>
          </AuthenticatedLayout>
        </ProtectedRoute>
      ),
    },
    {
      path: '/tasks/:id',
      element: (
        <ProtectedRoute isAuthenticated={isAuthenticated}>
          <AuthenticatedLayout onLogout={onLogout}>
            <SuspenseWrapper>
              <TaskDetailsPage />
            </SuspenseWrapper>
          </AuthenticatedLayout>
        </ProtectedRoute>
      ),
    },
    {
      path: '/tasks/:id/preview',
      element: (
        <ProtectedRoute isAuthenticated={isAuthenticated}>
          <AuthenticatedLayout onLogout={onLogout}>
            <SuspenseWrapper>
              <TaskPreviewPage />
            </SuspenseWrapper>
          </AuthenticatedLayout>
        </ProtectedRoute>
      ),
    },
    // Billing Cuts Routes
    {
      path: '/billing-cuts',
      element: (
        <ProtectedRoute isAuthenticated={isAuthenticated}>
          <AuthenticatedLayout onLogout={onLogout}>
            <SuspenseWrapper>
              <BillingCutsListPage />
            </SuspenseWrapper>
          </AuthenticatedLayout>
        </ProtectedRoute>
      ),
    },
    {
      path: '/billing-cuts/generate',
      element: (
        <ProtectedRoute isAuthenticated={isAuthenticated}>
          <AuthenticatedLayout onLogout={onLogout}>
            <SuspenseWrapper>
              <GenerateBillingCutPage />
            </SuspenseWrapper>
          </AuthenticatedLayout>
        </ProtectedRoute>
      ),
    },
    {
      path: '/billing-cuts/:id',
      element: (
        <ProtectedRoute isAuthenticated={isAuthenticated}>
          <AuthenticatedLayout onLogout={onLogout}>
            <SuspenseWrapper>
              <BillingCutDetailPage />
            </SuspenseWrapper>
          </AuthenticatedLayout>
        </ProtectedRoute>
      ),
    },
    // {
    //   path: '/documents',
    //   element: (
    //     <ProtectedRoute isAuthenticated={isAuthenticated}>
    //       <AuthenticatedLayout onLogout={onLogout}>
    //         <SuspenseWrapper>
    //           <DocumentsPage />
    //         </SuspenseWrapper>
    //       </AuthenticatedLayout>
    //     </ProtectedRoute>
    //   ),
    // },
    // {
    //   path: '/invoices',
    //   element: (
    //     <ProtectedRoute isAuthenticated={isAuthenticated}>
    //       <AuthenticatedLayout onLogout={onLogout}>
    //         <SuspenseWrapper>
    //           <InvoicesPage />
    //         </SuspenseWrapper>
    //       </AuthenticatedLayout>
    //     </ProtectedRoute>
    //   ),
    // },
    // {
    //   path: '/payments',
    //   element: (
    //     <ProtectedRoute isAuthenticated={isAuthenticated}>
    //       <AuthenticatedLayout onLogout={onLogout}>
    //         <SuspenseWrapper>
    //           <PaymentsPage />
    //         </SuspenseWrapper>
    //       </AuthenticatedLayout>
    //     </ProtectedRoute>
    //   ),
    // },
    // Directory — Phase 4 nested route migration.
    // /directory               -> redirect to /directory/insurer
    // /directory/:type         -> tab + list, no detail selected
    // /directory/:type/:id     -> tab + list + detail. Browser back-button
    //                              and deep links work for free.
    {
      path: '/directory',
      element: (
        <ProtectedRoute isAuthenticated={isAuthenticated}>
          <Navigate to="/directory/insurer" replace />
        </ProtectedRoute>
      ),
    },
    {
      path: '/directory/:type',
      element: (
        <ProtectedRoute isAuthenticated={isAuthenticated}>
          <AuthenticatedLayout onLogout={onLogout}>
            <SuspenseWrapper>
              <DirectoryPage />
            </SuspenseWrapper>
          </AuthenticatedLayout>
        </ProtectedRoute>
      ),
    },
    {
      path: '/directory/:type/:id',
      element: (
        <ProtectedRoute isAuthenticated={isAuthenticated}>
          <AuthenticatedLayout onLogout={onLogout}>
            <SuspenseWrapper>
              <DirectoryPage />
            </SuspenseWrapper>
          </AuthenticatedLayout>
        </ProtectedRoute>
      ),
    },
    {
      path: '/tasks',
      element: (
        <ProtectedRoute isAuthenticated={isAuthenticated}>
          <AuthenticatedLayout onLogout={onLogout}>
            <SuspenseWrapper>
              <TasksListPage />
            </SuspenseWrapper>
          </AuthenticatedLayout>
        </ProtectedRoute>
      ),
    },
    {
      path: '/tasks/:id',
      element: (
        <ProtectedRoute isAuthenticated={isAuthenticated}>
          <AuthenticatedLayout onLogout={onLogout}>
            <SuspenseWrapper>
              {/* TODO: Create TaskDetailPage */}
              <div className="p-6">
                <h1 className="text-2xl font-bold">Task Detail Page</h1>
                <p className="mt-4 text-gray-600">Coming soon...</p>
              </div>
            </SuspenseWrapper>
          </AuthenticatedLayout>
        </ProtectedRoute>
      ),
    },
    // {
    //   path: '/reports',
    //   element: (
    //     <ProtectedRoute isAuthenticated={isAuthenticated}>
    //       <AuthenticatedLayout onLogout={onLogout}>
    //         <SuspenseWrapper>
    //           <ReportsPage />
    //         </SuspenseWrapper>
    //       </AuthenticatedLayout>
    //     </ProtectedRoute>
    //   ),
    // },
    // {
    //   path: '/settings',
    //   element: (
    //     <ProtectedRoute isAuthenticated={isAuthenticated}>
    //       <AuthenticatedLayout onLogout={onLogout}>
    //         <SuspenseWrapper>
    //           <SettingsPage />
    //         </SuspenseWrapper>
    //       </AuthenticatedLayout>
    //     </ProtectedRoute>
    //   ),
    // },
    {
      path: '*',
      element: (
        <SuspenseWrapper>
          <Navigate to={isAuthenticated ? '/dashboard' : '/login'} replace />
        </SuspenseWrapper>
      ),
    },
  ];
}
